import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { fetchTrustSummary } from "../../lib/api";

// Green gradient matching TrustBadge.jsx
const RING_COLOR = (s) => {
  if (s >= 9.5) return "#2E7D4F";
  if (s >= 9.0) return "#3D8F5B";
  if (s >= 8.5) return "#4A9E6B";
  if (s >= 8.0) return "#7AB88C";
  if (s >= 6.0) return "#C9A96E";
  return "#C47070";
};
const SCORE_COLOR = (s) => {
  if (s >= 9.5) return "#2E7D4F";
  if (s >= 9.0) return "#3D8F5B";
  if (s >= 8.5) return "#4A9E6B";
  if (s >= 8.0) return "#7AB88C";
  if (s >= 6.0) return "#B8860B";
  return "#B85450";
};
const TRACK = "#F3E8EF";

/**
 * Floating aggregate trust badge — bottom-left of the Home page.
 * Shows the average score of every scored product + total count.
 * Clicking navigates to /top-formules for full details.
 */
export const GlobalTrustBadge = () => {
  const [data, setData] = useState(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    fetchTrustSummary().then(setData).catch(() => setData(null));
    const t = setTimeout(() => setMounted(true), 200);
    return () => clearTimeout(t);
  }, []);

  if (!data || !data.total) return null;
  const s = Number(data.average || 0);
  const ring = RING_COLOR(s);
  const scoreCol = SCORE_COLOR(s);

  // Ring geometry (44px)
  const size = 44, stroke = 3.2;
  const r = (size - stroke) / 2 - 2;
  const c = 2 * Math.PI * r;
  const dash = (s / 10) * c;

  return createPortal(
    <Link
      to="/top-formules"
      aria-label={`Score moyen ${s.toFixed(1)} sur 10 — ${data.total} compositions analysées`}
      data-testid="global-trust-badge"
      className={`global-trust-badge${s >= 9 ? " global-trust-badge--pulsing" : ""}`}
      style={{
        position: "fixed",
        left: 24,
        bottom: 24,
        zIndex: 40,
        display: "inline-flex",
        alignItems: "center",
        gap: 12,
        padding: "8px 16px 8px 8px",
        background: "rgba(255,255,255,0.96)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        border: "1px solid #E5D6E3",
        borderRadius: 999,
        boxShadow: "0 10px 30px -6px rgba(74,44,90,0.18), 0 2px 6px rgba(45,27,46,0.06)",
        textDecoration: "none",
        color: "#2D1B2E",
        fontFamily: "'Poppins', system-ui, sans-serif",
        opacity: mounted ? 1 : 0,
        transform: mounted ? "translateY(0)" : "translateY(20px)",
        transition: "opacity .5s ease .1s, transform .5s cubic-bezier(.2,.7,.3,1) .1s, box-shadow .2s, background .2s",
      }}
    >
      {/* Score ring */}
      <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: "rotate(-90deg)" }}>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={TRACK} strokeWidth={stroke} />
          <circle
            cx={size / 2} cy={size / 2} r={r} fill="none"
            stroke={ring} strokeWidth={stroke} strokeLinecap="round"
            strokeDasharray={`${dash} ${c - dash}`}
          />
        </svg>
        <span
          style={{
            position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "'Playfair Display', serif", fontSize: 13.5, fontWeight: 600, color: scoreCol,
          }}
        >
          {s.toFixed(1)}
        </span>
      </div>

      {/* Text */}
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.15, minWidth: 0 }}>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: "#2D1B2E", letterSpacing: ".005em" }}>
          Formules vérifiées
        </span>
        <span style={{ fontSize: 10.5, color: "#8B7B8E", marginTop: 2 }}>
          {data.total} compositions analysées
        </span>
      </div>

      {/* Shield icon */}
      <div
        aria-hidden
        style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          width: 26, height: 26, borderRadius: 999,
          background: "#FDF0F5", color: "#9B4A6B", flexShrink: 0,
        }}
      >
        <ShieldCheck size={14} strokeWidth={1.6} />
      </div>
    </Link>,
    document.body
  );
};

export default GlobalTrustBadge;
